import { Box, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { cx } from '@/styles/muiTheme';
import { blurredMenuStyles } from '@/shared/components/PageComponents/NavigationMenu';
import { getEventStyle } from '../../services/clipEventStyle';
import GameSelect, { GameFilterOption } from './GameSelect';
import PlayerSelect, { PlayerFilterOption } from './PlayerSelect';

export type GroupMode = 'all' | 'game' | 'player' | 'event';

/**
 * Authorship filter. 'me' and 'others' are both about people — a clip cut by a
 * rule is reachable only through 'automation', so the three do not overlap.
 */
export type CreatorFilter = 'all' | 'me' | 'others' | 'automation';

const CREATORS: { value: CreatorFilter; label: string }[] = [
  { value: 'all', label: 'Anyone' },
  { value: 'me', label: 'Created by me' },
  { value: 'others', label: 'Created by others' },
];

// Listed separately because automated clips are themselves flag-gated: with the
// flag off there are none to match, so the option would only ever empty the grid.
const AUTOMATION_CREATOR: { value: CreatorFilter; label: string } = {
  value: 'automation',
  label: 'Created by automation',
};

/** Plain text option, for the controls that render no imagery. */
export type FilterOption = { value: string; label: string };

type Props = {
  mode: GroupMode;
  onModeChange: (mode: GroupMode) => void;
  player: string;
  onPlayerChange: (player: string) => void;
  playerOptions: PlayerFilterOption[];
  season: string;
  onSeasonChange: (season: string) => void;
  seasonOptions: FilterOption[];
  game: string;
  onGameChange: (game: string) => void;
  gameOptions: GameFilterOption[];
  creator: CreatorFilter;
  onCreatorChange: (creator: CreatorFilter) => void;
  showAutomationCreator: boolean;
  event: string;
  onEventChange: (event: string) => void;
  eventOptions: FilterOption[];
  query: string;
  onQueryChange: (query: string) => void;
  countLabel: string;
};

const MODES: { value: GroupMode; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'game', label: 'Game' },
  { value: 'player', label: 'Player tracked' },
  { value: 'event', label: 'Event type' },
];

const labelSx = { fontSize: 11, color: cx.text.tertiary, letterSpacing: '0.15px' } as const;

// The season picker stays a native select: it is plain text, and MUI's own field
// overrides own the padding model for MUI inputs at 38px where this row is 32px.
// The player and game pickers are Autocompletes because they render imagery,
// which an <option> cannot hold; `filterFieldSx` trims them to match.
const selectSx = {
  height: 32,
  backgroundColor: cx.bg.surface2,
  color: cx.text.primary,
  border: `1px solid ${cx.border.strong}`,
  borderRadius: '4px',
  padding: '0 8px',
  fontSize: 12,
  fontFamily: 'inherit',
  outline: 'none',
  cursor: 'pointer',
} as const;

const ClipLibraryFilters = ({
  mode,
  onModeChange,
  player,
  onPlayerChange,
  playerOptions,
  season,
  onSeasonChange,
  seasonOptions,
  game,
  onGameChange,
  gameOptions,
  creator,
  onCreatorChange,
  showAutomationCreator,
  event,
  onEventChange,
  eventOptions,
  query,
  onQueryChange,
  countLabel,
}: Props) => (
  <Box
    sx={{
      // Same frosted glass as the nav menu, so clips scrolling under the sticky
      // bar show through it.
      ...blurredMenuStyles,
      borderRadius: '4px',
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      position: 'sticky',
      top: 8,
      zIndex: 5,
    }}
  >
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography sx={labelSx}>Display by</Typography>
        <Box sx={{ display: 'flex', gap: '4px', backgroundColor: cx.bg.surface3, borderRadius: '100px', padding: '4px' }}>
          {MODES.map((option) => (
            <Box
              key={option.value}
              component="button"
              type="button"
              onClick={() => onModeChange(option.value)}
              sx={{
                height: 28,
                padding: '0 14px',
                border: 'none',
                borderRadius: '100px',
                cursor: 'pointer',
                fontSize: 12,
                letterSpacing: '0.15px',
                backgroundColor: mode === option.value ? cx.blue.main : 'transparent',
                color: mode === option.value ? cx.text.primary : cx.text.secondary,
              }}
            >
              {option.label}
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ width: '1px', height: 24, backgroundColor: cx.border.subtle }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography sx={labelSx}>Player tracked</Typography>
        <PlayerSelect value={player} onChange={onPlayerChange} options={playerOptions} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography sx={labelSx}>Season</Typography>
        <Box
          component="select"
          value={season}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onSeasonChange(e.target.value)}
          sx={{ ...selectSx, minWidth: 150 }}
        >
          <option value="all">All seasons</option>
          {seasonOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography sx={labelSx}>Game</Typography>
        <GameSelect value={game} onChange={onGameChange} options={gameOptions} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography sx={labelSx}>Created by</Typography>
        <Box
          component="select"
          value={creator}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            onCreatorChange(e.target.value as CreatorFilter)
          }
          sx={{ ...selectSx, minWidth: 150 }}
        >
          {[...CREATORS, ...(showAutomationCreator ? [AUTOMATION_CREATOR] : [])].map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          height: 32,
          minWidth: 200,
          flex: 1,
          maxWidth: 360,
          border: `1px solid ${cx.border.strong}`,
          borderRadius: '4px',
          backgroundColor: cx.bg.surface2,
          padding: '0 12px',
        }}
      >
        <SearchIcon sx={{ fontSize: 16, color: cx.text.tertiary }} />
        <Box
          component="input"
          value={query}
          placeholder="Search"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onQueryChange(e.target.value)}
          sx={{
            flex: 1,
            minWidth: 0,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: cx.text.primary,
            fontSize: 12,
            fontFamily: 'inherit',
            letterSpacing: '0.15px',
          }}
        />
      </Box>

      <Typography sx={labelSx}>{countLabel}</Typography>
    </Box>

    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
      {[{ value: 'all', label: 'All events' }, ...eventOptions].map((option) => {
        const isActive = event === option.value;
        const dot = option.value === 'all' ? cx.text.primary : getEventStyle(option.value).dot;

        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            onClick={() => onEventChange(option.value)}
            sx={{
              height: 28,
              padding: '0 12px',
              borderRadius: '100px',
              cursor: 'pointer',
              fontSize: 12,
              letterSpacing: '0.15px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: isActive ? cx.light[200] : cx.bg.surface2,
              color: isActive ? cx.text.onLight : cx.text.secondary,
              border: `1px solid ${isActive ? cx.light[200] : cx.border.subtle}`,
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: dot }} />
            {option.label}
          </Box>
        );
      })}
    </Box>
  </Box>
);

export default ClipLibraryFilters;
